/**
 * features/finance/RequisitionsHub.tsx
 * ------------------------------------------------------------------
 * ASKING BEFORE BUYING.
 *
 * The last point in the chain where the answer can still be no. An
 * order tells a supplier something; a receipt records what came; a
 * voucher pays for it. All three describe things that have already
 * happened. Only this one is a question.
 *
 * IT HAS TO BEAT PAPER OR NOBODY WILL USE IT, and a purchasing control
 * everybody routes around is not a control. So the form asks for a
 * title, the items, and who should approve — nothing else is required.
 * Department and justification are there for the requester who wants to
 * make their case, not demanded from the one who does not.
 *
 * WHOSE DESK IS IT ON. The default view is not a list of requisitions,
 * it is a list of what is waiting and who is holding it up, longest
 * first. A requisition nobody has approved and nobody has refused is the
 * most expensive state in the chain — the department believes it is
 * coming and nothing has been ordered.
 *
 * APPROVAL IS A ROLE, NEVER A PERSON. Whoever holds the operations
 * capability approves. Nothing here knows anybody's name.
 * ------------------------------------------------------------------ */
import { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot, query } from 'firebase/firestore';
import {
  FileQuestion, Plus, Clock, CheckCircle2, X, ArrowLeft, Trash2, AlertTriangle,
  MessageSquare,
} from 'lucide-react';
import { Spinner } from '@/components/ui/primitives';
import { useAuth } from '@/contexts/AuthContext';
import { useData } from '@/contexts/DataContext';
import { db } from '@/lib/firebase';
import {
  REQ_COLLECTIONS, REQ_STATUS_LABEL, REQ_EVENT_VERB, reqStatusSummary,
  availableReqActions, canRejectReq, reqActionProblem, requisitionsWaiting,
  reqEstTotal, normaliseReqLine,
  type Requisition, type ReqLine, type ReqActor, type ReqStatus, type ReqEvent,
} from '@/lib/requisitions';
import {
  createRequisition, transitionRequisition, rejectRequisition,
  commentOnRequisition, fetchRequisitionEvents,
} from '@/features/finance/reqWrite';
import {
  PageHeader, SoftCard, PanelHeader, StatChip, glassFieldClass, btnPrimary, btnGhost,
  modalBackdrop, modalSurface,
} from '@/features/finance/ui';

const money = (n: number) =>
  n.toLocaleString('en-BW', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const STATUS_TONE: Record<ReqStatus, string> = {
  draft: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
  submitted: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  approver_approved: 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200',
  operations_approved: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
  ordered: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-200',
  closed: 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400',
  rejected: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
};

const blankLine = (): ReqLine => ({ description: '', qty: 1, estUnitCost: 0 });

export default function RequisitionsHub() {
  const { userData, permissions } = useAuth();
  const [reqs, setReqs] = useState<Requisition[] | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [tab, setTab] = useState<'waiting' | 'mine' | 'all'>('waiting');

  useEffect(() => onSnapshot(
    query(collection(db, REQ_COLLECTIONS.requisitions)),
    (s) => setReqs(s.docs.map((d) => ({ id: d.id, ...d.data() }) as Requisition)),
    () => setReqs([]),
  ), []);

  /**
   * The capability, not the person. `canApproveVouchers` IS the authority
   * to commit the school's money — the same one that approves a payment —
   * so operations approval reuses it rather than inventing a parallel
   * permission that could drift out of step with it.
   */
  const actor: ReqActor & { name?: string } = useMemo(() => ({
    uid: userData?.uid ?? '',
    name: userData?.displayName,
    teacherId: userData?.linkedTeacherId,
    canRaise: true,
    canApproveOperations: Boolean(permissions?.canApproveVouchers),
    canProcure: Boolean(permissions?.canRaiseVouchers),
  }), [userData, permissions]);

  const waiting = useMemo(() => requisitionsWaiting(reqs ?? []), [reqs]);

  /** What is waiting on THIS person specifically — the reason they opened it. */
  const mineToAct = useMemo(
    () => (reqs ?? []).filter((r) => availableReqActions(r, actor).length > 0),
    [reqs, actor],
  );

  const shown = useMemo(() => {
    const all = [...(reqs ?? [])].sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? ''));
    if (tab === 'mine') return all.filter((r) => r.requestedBy === actor.uid);
    if (tab === 'waiting') return waiting.map((w) => w.req);
    return all;
  }, [reqs, tab, waiting, actor.uid]);

  const open = (reqs ?? []).find((r) => r.id === openId);

  if (reqs === null) return <div className="p-6 flex justify-center"><Spinner /></div>;

  if (open) {
    return (
      <RequisitionDetail
        req={open} actor={actor}
        onBack={() => setOpenId(null)} onError={setErr}
      />
    );
  }

  const daysFor = (id: string) => waiting.find((w) => w.req.id === id)?.days;

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      <PageHeader
        icon={FileQuestion}
        tone="navy"
        title="Requisitions"
        subtitle="Ask before the money is committed — the one point where the answer can still be no"
        actions={(
          <button type="button" className={btnPrimary} onClick={() => setCreating(true)}>
            <Plus size={15} /> New requisition
          </button>
        )}
      />

      {err && (
        <SoftCard className="mb-4 border-l-4 border-red-400">
          <p className="text-sm text-red-700 dark:text-red-300 flex items-start gap-2">
            <AlertTriangle size={15} className="mt-0.5 shrink-0" />{err}
          </p>
        </SoftCard>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
        <StatChip
          tone={mineToAct.length > 0 ? 'amber' : 'slate'}
          icon={Clock} value={String(mineToAct.length)} label="Waiting on you" />
        <StatChip tone="navy" value={String(waiting.length)} label="Waiting on somebody" />
        <StatChip tone="slate"
          value={money(waiting.reduce((a, w) => a + (Number(w.req.estTotal) || 0), 0))}
          label="Estimated value in the queue" />
      </div>

      <div className="flex gap-1 mb-4">
        {([
          ['waiting', `In the queue (${waiting.length})`],
          ['mine', 'Mine'],
          ['all', 'Everything'],
        ] as const).map(([k, label]) => (
          <button
            key={k} type="button" onClick={() => setTab(k)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              tab === k
                ? 'bg-primary text-white'
                : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800'
            }`}
          >{label}</button>
        ))}
      </div>

      <SoftCard pad="p-0">
        <PanelHeader
          icon={FileQuestion} tone="navy"
          title={tab === 'waiting' ? 'Whose desk it is on' : 'Requisitions'}
          subtitle={tab === 'waiting'
            ? 'Longest wait first — nobody has said yes and nobody has said no'
            : 'Newest first'}
        />

        {shown.length === 0 ? (
          <p className="p-6 text-sm text-gray-500 text-center">
            {tab === 'waiting'
              ? 'Nothing is waiting on anybody.'
              : 'Nothing here yet.'}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-gray-400 border-b border-gray-100 dark:border-gray-700/70">
                  <th className="py-2 pl-4 pr-2 font-semibold">Requisition</th>
                  <th className="px-2 font-semibold">Raised by</th>
                  <th className="px-2 font-semibold">Where it is</th>
                  <th className="px-2 font-semibold text-right">Estimate</th>
                  <th className="px-2 pr-4 font-semibold text-right">Waiting</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((r) => {
                  const days = daysFor(r.id);
                  const yours = availableReqActions(r, actor).length > 0;
                  return (
                    <tr
                      key={r.id} onClick={() => setOpenId(r.id)}
                      className="border-b border-gray-50 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/40 cursor-pointer"
                    >
                      <td className="py-2.5 pl-4 pr-2">
                        <span className="font-medium text-gray-900 dark:text-white">{r.title}</span>
                        {yours && (
                          <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                            your turn
                          </span>
                        )}
                        <span className="block font-mono text-[11px] text-gray-400">{r.reqNo}</span>
                      </td>
                      <td className="px-2 text-gray-600 dark:text-gray-300 truncate max-w-[140px]">
                        {r.requestedByName ?? '—'}
                      </td>
                      <td className="px-2">
                        <span className={`text-[11px] px-1.5 py-0.5 rounded font-medium ${STATUS_TONE[r.currentStatus]}`}>
                          {REQ_STATUS_LABEL[r.currentStatus]}
                        </span>
                      </td>
                      <td className="px-2 text-right tabular-nums text-gray-600 dark:text-gray-300">
                        {money(r.estTotal)}
                      </td>
                      <td className={`px-2 pr-4 text-right tabular-nums ${
                        (days ?? 0) > 7 ? 'text-amber-700 dark:text-amber-300 font-medium' : 'text-gray-500'
                      }`}>
                        {days === undefined ? '' : `${days}d`}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </SoftCard>

      {creating && (
        <NewRequisitionModal
          actor={actor}
          onClose={() => setCreating(false)}
          onError={setErr}
        />
      )}
    </div>
  );
}

/* -------------------------------- detail -------------------------------- */

function RequisitionDetail({
  req, actor, onBack, onError,
}: {
  req: Requisition;
  actor: ReqActor & { name?: string };
  onBack: () => void;
  onError: (m: string | null) => void;
}) {
  const [events, setEvents] = useState<ReqEvent[]>([]);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');

  useEffect(() => {
    fetchRequisitionEvents(req.id).then(setEvents).catch(() => setEvents([]));
  }, [req.id, req.currentStatus]);

  const actions = availableReqActions(req, actor);

  /* Why an action is NOT offered, for the person who expected it. A button
     that silently is not there is indistinguishable from a broken screen. */
  const blockedReason = actions.length === 0
    ? reqActionProblem(req, req.currentStatus === 'submitted' ? 'approverApprove'
      : req.currentStatus === 'approver_approved' ? 'operationsApprove'
      : req.currentStatus === 'draft' ? 'submit' : 'close', actor)
    : null;

  async function run(fn: () => Promise<unknown>) {
    setBusy(true); onError(null);
    try { await fn(); } catch (e) { onError((e as Error).message); }
    setBusy(false);
  }

  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto">
      <button type="button" onClick={onBack} className={`${btnGhost} mb-4`}>
        <ArrowLeft size={15} /> All requisitions
      </button>

      <PageHeader
        icon={FileQuestion} tone="navy"
        title={req.title}
        subtitle={`${req.reqNo} · ${reqStatusSummary(req)}`}
        actions={(
          <div className="flex gap-2">
            {actions.map((a) => (
              <button key={a.key} type="button" disabled={busy} className={btnPrimary}
                onClick={() => run(() => transitionRequisition(req, a.key as never, actor))}>
                {a.label}
              </button>
            ))}
            {canRejectReq(req, actor) && (
              <button type="button" disabled={busy} className={btnGhost}
                onClick={() => {
                  const reason = window.prompt('Why is this being refused?');
                  if (reason) run(() => rejectRequisition(req, reason, actor));
                }}>
                Reject
              </button>
            )}
          </div>
        )}
      />

      {blockedReason && !['closed', 'rejected'].includes(req.currentStatus) && (
        <SoftCard className="mb-4 border-l-4 border-gray-300">
          <p className="text-sm text-gray-600 dark:text-gray-300">{blockedReason}</p>
        </SoftCard>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
        <StatChip tone="navy" value={money(req.estTotal)} label="Estimated cost" />
        <StatChip tone="slate" value={req.requestedByName ?? '—'} label="Raised by" />
        <StatChip tone="slate" value={req.approverName ?? '—'} label="Nominated approver" />
      </div>

      {req.justification && (
        <SoftCard className="mb-5">
          <p className="text-[11px] uppercase tracking-wide text-gray-400 mb-1">Why</p>
          <p className="text-sm text-gray-700 dark:text-gray-200 whitespace-pre-wrap">
            {req.justification}
          </p>
        </SoftCard>
      )}

      <SoftCard pad="p-0" className="mb-5">
        <PanelHeader icon={FileQuestion} title="What is being asked for" tone="navy"
          subtitle="An estimate — the real figure is the purchase order" />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-gray-400 border-b border-gray-100 dark:border-gray-700/70">
                <th className="py-2 pl-4 pr-2 font-semibold">Item</th>
                <th className="px-2 font-semibold text-right">Qty</th>
                <th className="px-2 font-semibold text-right">Est. each</th>
                <th className="px-2 pr-4 font-semibold text-right">Est. total</th>
              </tr>
            </thead>
            <tbody>
              {req.lines.map((l, i) => (
                <tr key={i} className="border-b border-gray-50 dark:border-gray-800">
                  <td className="py-2 pl-4 pr-2 text-gray-800 dark:text-gray-100">{l.description}</td>
                  <td className="px-2 text-right tabular-nums">{l.qty}</td>
                  <td className="px-2 text-right tabular-nums text-gray-500">
                    {l.estUnitCost ? money(l.estUnitCost) : '—'}
                  </td>
                  <td className="px-2 pr-4 text-right tabular-nums">
                    {money(l.estLineTotal ?? 0)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SoftCard>

      <SoftCard pad="p-0">
        <PanelHeader icon={MessageSquare} title="History" tone="navy"
          subtitle="Append-only — this is the evidence, not the status field" />
        <div className="p-4 space-y-2">
          {events.length === 0 && <p className="text-sm text-gray-500">Nothing recorded yet.</p>}
          {events.map((e) => (
            <div key={e.id} className="text-[13px] border-l-2 border-gray-200 dark:border-gray-700 pl-3 py-0.5">
              <span className="font-medium text-gray-800 dark:text-gray-100">
                {REQ_EVENT_VERB[e.type]}
              </span>
              <span className="text-gray-500"> by {e.byName || 'someone'} · {e.at?.slice(0, 10)}</span>
              {e.comment && (
                <p className="text-gray-600 dark:text-gray-300 mt-0.5">{e.comment}</p>
              )}
            </div>
          ))}

          <div className="flex gap-2 pt-2">
            <input
              value={note} onChange={(ev) => setNote(ev.target.value)}
              placeholder="Add a note…" className={glassFieldClass()}
            />
            <button
              type="button" className={btnGhost} disabled={busy || !note.trim()}
              onClick={() => run(async () => {
                await commentOnRequisition(req.id, note, actor);
                setNote('');
                setEvents(await fetchRequisitionEvents(req.id));
              })}
            >Add</button>
          </div>
        </div>
      </SoftCard>
    </div>
  );
}

/* ------------------------------- new ------------------------------- */

function NewRequisitionModal({
  actor, onClose, onError,
}: {
  actor: ReqActor & { name?: string };
  onClose: () => void;
  onError: (m: string | null) => void;
}) {
  const { teachers } = useData();
  const [title, setTitle] = useState('');
  const [department, setDepartment] = useState('');
  const [justification, setJustification] = useState('');
  const [approver, setApprover] = useState('');
  const [lines, setLines] = useState<ReqLine[]>([blankLine()]);
  const [busy, setBusy] = useState(false);

  const priced = lines.map(normaliseReqLine);
  const total = reqEstTotal(priced);

  /* Anybody on the staff may be nominated. A teacher can legitimately
     oversee an obscure area and approve for somebody senior to them, so
     the list is not filtered by seniority — the control is that
     operations reviews every one afterwards.

     NOMINATED BY TEACHER RECORD, NOT BY uid. The link runs from the user
     to the teacher (AppUser.linkedTeacherId), and a teacher raising a
     requisition cannot read the users directory at all — Firestore rules
     forbid it. So the teacher id is the key that can actually be
     resolved here, and lib/requisitions matches on either. */
  const candidates = useMemo(
    () => [...teachers]
      .filter((t) => t.id !== actor.teacherId)
      .sort((a, b) => (a.displayName ?? '').localeCompare(b.displayName ?? '')),
    [teachers, actor.teacherId],
  );

  function setLine(i: number, patch: Partial<ReqLine>) {
    setLines(lines.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  }

  async function save() {
    setBusy(true); onError(null);
    try {
      const chosen = candidates.find((t) => t.id === approver);
      await createRequisition({
        title,
        department: department || undefined,
        justification: justification || undefined,
        lines: priced,
        approverId: chosen?.id,
        approverName: chosen?.displayName,
        actor: { uid: actor.uid, name: actor.name },
      });
      onClose();
    } catch (e) {
      onError((e as Error).message);
    }
    setBusy(false);
  }

  return (
    <div className={modalBackdrop} onClick={onClose}>
      <div className={modalSurface('max-w-3xl w-full')} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-gray-700">
          <h3 className="font-bold text-gray-900 dark:text-white">New requisition</h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={18} />
          </button>
        </div>

        <div className="p-4 space-y-3 max-h-[65vh] overflow-y-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[11px] uppercase tracking-wide text-gray-400">What is it for</span>
              <input value={title} onChange={(e) => setTitle(e.target.value)}
                className={glassFieldClass('mt-1')} placeholder="Science department consumables" />
            </label>
            <label className="block">
              <span className="text-[11px] uppercase tracking-wide text-gray-400">
                Who should approve
              </span>
              <select value={approver} onChange={(e) => setApprover(e.target.value)}
                className={glassFieldClass('mt-1')}>
                <option value="">— choose —</option>
                {candidates.map((t) => (
                  <option key={t.id} value={t.id}>{t.displayName}</option>
                ))}
              </select>
            </label>
          </div>

          <label className="block">
            <span className="text-[11px] uppercase tracking-wide text-gray-400">
              Department <span className="normal-case text-gray-400">(optional)</span>
            </span>
            <input value={department} onChange={(e) => setDepartment(e.target.value)}
              className={glassFieldClass('mt-1')} />
          </label>

          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-gray-400">
                <th className="py-1 font-semibold">Item</th>
                <th className="font-semibold text-right w-20">Qty</th>
                <th className="font-semibold text-right w-28">Est. each</th>
                <th className="w-8" />
              </tr>
            </thead>
            <tbody>
              {lines.map((l, i) => (
                <tr key={i} className="border-t border-gray-100 dark:border-gray-800">
                  <td className="py-1.5 pr-2">
                    <input value={l.description}
                      onChange={(e) => setLine(i, { description: e.target.value })}
                      className={glassFieldClass()} placeholder="What is needed" />
                  </td>
                  <td className="pr-2">
                    <input type="number" step="any" value={l.qty}
                      onChange={(e) => setLine(i, { qty: Number(e.target.value) })}
                      className={glassFieldClass('text-right')} />
                  </td>
                  <td className="pr-2">
                    <input type="number" step="0.01" value={l.estUnitCost ?? ''}
                      onChange={(e) => setLine(i, { estUnitCost: Number(e.target.value) })}
                      className={glassFieldClass('text-right')} placeholder="—" />
                  </td>
                  <td>
                    {lines.length > 1 && (
                      <button type="button" className="text-gray-400 hover:text-red-600"
                        onClick={() => setLines(lines.filter((_, idx) => idx !== i))}>
                        <Trash2 size={15} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <button type="button" className={btnGhost} onClick={() => setLines([...lines, blankLine()])}>
            <Plus size={14} /> Add an item
          </button>

          <label className="block">
            <span className="text-[11px] uppercase tracking-wide text-gray-400">
              Why <span className="normal-case text-gray-400">(optional, but it is what the approver reads)</span>
            </span>
            <textarea value={justification} onChange={(e) => setJustification(e.target.value)}
              rows={2} className={glassFieldClass('mt-1')} />
          </label>

          <p className="text-[12px] text-gray-500 flex items-start gap-1.5">
            <CheckCircle2 size={13} className="mt-0.5 shrink-0 text-gray-400" />
            A price here is an estimate. The figure that counts is the purchase order,
            so a guess is better than leaving it blank.
          </p>
        </div>

        <div className="flex items-center justify-between gap-2 p-4 border-t border-gray-100 dark:border-gray-700">
          <span className="text-sm text-gray-500">
            Estimated <strong className="text-gray-900 dark:text-white tabular-nums">{money(total)}</strong>
          </span>
          <div className="flex gap-2">
            <button type="button" className={btnGhost} onClick={onClose}>Cancel</button>
            <button type="button" className={btnPrimary} disabled={busy} onClick={save}>
              {busy ? 'Saving…' : 'Save as draft'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
